/**
 * Hook to access and control the Babylon engine
 *
 * IMPORTANT: Engine objects are NOT stored in React state because:
 * 1. Engine objects are complex with internal state
 * 2. React 19 dev mode tries to serialize state for debugging
 *
 * Instead, we store a version counter and derive engine from the manager.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { AnyEngine } from "../../managers/engine/types";
import type { UseEngineReturn } from "../types";
import { useBabylonManager } from "./useBabylonManager";

/**
 * Hook to access and control the Babylon engine
 * @returns Engine instance and control functions
 */
export function useEngine(): UseEngineReturn {
  const { manager, initialize } = useBabylonManager();

  // Version counter triggers re-renders when engine changes, without storing Engine in state
  // This prevents React 19 dev mode from trying to serialize complex engine internals
  const [engineVersion, setEngineVersion] = useState(0);

  // Derive engine from manager on each render (cheap operation)
  // This keeps the Engine object out of React state
  const engine = useMemo<AnyEngine | null>(() => {
    // engineVersion dependency ensures we re-compute when engine changes
    if (!manager || engineVersion < 0) return null;
    return manager.getEngine();
  }, [manager, engineVersion]);

  const isReady = engine !== null;

  // Subscribe to engine events and bump version to trigger re-renders
  useEffect(() => {
    if (manager) {
      // Initial sync - bump version to ensure engine is current
      setEngineVersion((v) => v + 1);

      // Subscribe to engine events - bump version instead of storing engine
      const handleEngineCreated = () => {
        setEngineVersion((v) => v + 1);
      };

      const handleEngineDisposed = () => {
        setEngineVersion((v) => v + 1);
      };

      const unsubscribeCreated = manager.onEvent(
        "engineCreated",
        handleEngineCreated,
      );
      const unsubscribeDisposed = manager.onEvent(
        "engineDisposed",
        handleEngineDisposed,
      );

      return () => {
        unsubscribeCreated();
        unsubscribeDisposed();
      };
    }
  }, [manager]);

  // Create engine function
  const createEngine = useCallback(
    async (canvas: HTMLCanvasElement): Promise<AnyEngine> => {
      if (!manager) {
        throw new Error("BabylonManager is not initialized");
      }

      if (!manager.isInitialized && !manager.isInitializing) {
        await initialize();
      }

      await manager.waitForReady();

      const engineManager = manager.getEngineManager();
      if (!engineManager) {
        throw new Error("EngineManager is not available");
      }

      // Create engine with canvas
      const engine = await manager.createEngine({
        canvas,
        type: "webgl", // Default to WebGL
      });

      return engine;
    },
    [manager],
  );

  // Start render loop
  const startRenderLoop = useCallback(
    (callback: () => void) => {
      if (!manager) {
        throw new Error("BabylonManager is not initialized");
      }

      if (!manager.isReady) {
        throw new Error(
          "BabylonManager is not ready. Await initialization before starting the render loop.",
        );
      }

      const engineManager = manager.getEngineManager();
      if (!engineManager) {
        throw new Error("EngineManager is not available");
      }

      engineManager.startRenderLoop(callback);
    },
    [manager],
  );

  // Stop render loop
  const stopRenderLoop = useCallback(() => {
    if (!manager) {
      return;
    }

    const engineManager = manager.getEngineManager();
    if (engineManager) {
      engineManager.stopRenderLoop();
    }
  }, [manager]);

  // Resize engine
  const resize = useCallback(() => {
    if (engine && "resize" in engine) {
      engine.resize();
    }
  }, [engine]);

  return {
    createEngine,
    engine,
    isReady,
    resize,
    startRenderLoop,
    stopRenderLoop,
  };
}
